import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Building2, Bot, Briefcase, ShoppingCart, GraduationCap, Heart,
  Plane, Car, Home, Utensils, Music, Camera, Gamepad2, Book,
  Coffee, Palette, Code, Database, Globe, Headphones, Mail,
  MessageSquare, Phone, Rocket, Shield, Star, Sun, Target,
  Tv, Users, Zap, Award, Bookmark, Calendar, Cloud, Compass,
  CreditCard, Film, Flag, Gift, Key, Layers, LifeBuoy, Map,
  Megaphone, Monitor, Package, PenTool, Percent, PieChart,
  Radio, Scissors, Send, Settings, ShoppingBag, Smile, Speaker,
  Tag, Thermometer, Truck, Umbrella, Video, Wifi, Wind, Wrench
} from 'lucide-react';

// Icon library with categories
export const ICON_LIBRARY = {
  business: [
    { name: 'Building2', icon: Building2, label: 'Building' },
    { name: 'Briefcase', icon: Briefcase, label: 'Briefcase' },
    { name: 'ShoppingCart', icon: ShoppingCart, label: 'Shopping' },
    { name: 'CreditCard', icon: CreditCard, label: 'Credit Card' },
    { name: 'PieChart', icon: PieChart, label: 'Analytics' },
    { name: 'Target', icon: Target, label: 'Target' },
    { name: 'Award', icon: Award, label: 'Award' },
    { name: 'Percent', icon: Percent, label: 'Percent' },
  ],
  tech: [
    { name: 'Bot', icon: Bot, label: 'Bot' },
    { name: 'Code', icon: Code, label: 'Code' },
    { name: 'Database', icon: Database, label: 'Database' },
    { name: 'Globe', icon: Globe, label: 'Globe' },
    { name: 'Monitor', icon: Monitor, label: 'Monitor' },
    { name: 'Rocket', icon: Rocket, label: 'Rocket' },
    { name: 'Zap', icon: Zap, label: 'Zap' },
    { name: 'Cloud', icon: Cloud, label: 'Cloud' },
    { name: 'Wifi', icon: Wifi, label: 'Wifi' },
    { name: 'Settings', icon: Settings, label: 'Settings' },
  ],
  communication: [
    { name: 'MessageSquare', icon: MessageSquare, label: 'Chat' },
    { name: 'Mail', icon: Mail, label: 'Mail' },
    { name: 'Phone', icon: Phone, label: 'Phone' },
    { name: 'Megaphone', icon: Megaphone, label: 'Announce' },
    { name: 'Send', icon: Send, label: 'Send' },
    { name: 'Radio', icon: Radio, label: 'Radio' },
    { name: 'Headphones', icon: Headphones, label: 'Support' },
    { name: 'Video', icon: Video, label: 'Video' },
  ],
  lifestyle: [
    { name: 'Heart', icon: Heart, label: 'Health' },
    { name: 'Home', icon: Home, label: 'Home' },
    { name: 'Utensils', icon: Utensils, label: 'Food' },
    { name: 'Coffee', icon: Coffee, label: 'Coffee' },
    { name: 'Music', icon: Music, label: 'Music' },
    { name: 'Camera', icon: Camera, label: 'Photo' },
    { name: 'Gamepad2', icon: Gamepad2, label: 'Gaming' },
    { name: 'Book', icon: Book, label: 'Education' },
    { name: 'GraduationCap', icon: GraduationCap, label: 'Learning' },
    { name: 'Smile', icon: Smile, label: 'Happy' },
  ],
  travel: [
    { name: 'Plane', icon: Plane, label: 'Travel' },
    { name: 'Car', icon: Car, label: 'Auto' },
    { name: 'Map', icon: Map, label: 'Map' },
    { name: 'Compass', icon: Compass, label: 'Explore' },
    { name: 'Truck', icon: Truck, label: 'Delivery' },
    { name: 'Umbrella', icon: Umbrella, label: 'Weather' },
  ],
  creative: [
    { name: 'Palette', icon: Palette, label: 'Design' },
    { name: 'PenTool', icon: PenTool, label: 'Draw' },
    { name: 'Film', icon: Film, label: 'Film' },
    { name: 'Scissors', icon: Scissors, label: 'Edit' },
    { name: 'Layers', icon: Layers, label: 'Layers' },
  ],
  other: [
    { name: 'Star', icon: Star, label: 'Star' },
    { name: 'Shield', icon: Shield, label: 'Security' },
    { name: 'Users', icon: Users, label: 'Team' },
    { name: 'Calendar', icon: Calendar, label: 'Calendar' },
    { name: 'Gift', icon: Gift, label: 'Gift' },
    { name: 'Key', icon: Key, label: 'Key' },
    { name: 'LifeBuoy', icon: LifeBuoy, label: 'Help' },
    { name: 'Package', icon: Package, label: 'Package' },
    { name: 'Flag', icon: Flag, label: 'Flag' },
    { name: 'Bookmark', icon: Bookmark, label: 'Save' },
    { name: 'Tag', icon: Tag, label: 'Tag' },
    { name: 'Wrench', icon: Wrench, label: 'Tool' },
    { name: 'Sun', icon: Sun, label: 'Light' },
  ],
};

// Flat list of all icons
export const ALL_ICONS = Object.values(ICON_LIBRARY).flat();

// Get icon component by name
export const getIconByName = (name) => {
  const iconData = ALL_ICONS.find(i => i.name === name);
  return iconData?.icon || Building2;
};

// Icon colors - Light mode friendly
export const ICON_COLORS = [
  { name: 'indigo', class: 'bg-indigo-100 text-indigo-600 border-indigo-200' },
  { name: 'purple', class: 'bg-purple-100 text-purple-600 border-purple-200' },
  { name: 'pink', class: 'bg-pink-100 text-pink-600 border-pink-200' },
  { name: 'red', class: 'bg-red-100 text-red-600 border-red-200' },
  { name: 'orange', class: 'bg-orange-100 text-orange-600 border-orange-200' },
  { name: 'amber', class: 'bg-amber-100 text-amber-600 border-amber-200' },
  { name: 'green', class: 'bg-green-100 text-green-600 border-green-200' },
  { name: 'teal', class: 'bg-teal-100 text-teal-600 border-teal-200' },
  { name: 'cyan', class: 'bg-cyan-100 text-cyan-600 border-cyan-200' },
  { name: 'blue', class: 'bg-blue-100 text-blue-600 border-blue-200' },
  { name: 'slate', class: 'bg-slate-100 text-slate-600 border-slate-200' },
];

export const getColorClass = (colorName) => {
  return ICON_COLORS.find(c => c.name === colorName)?.class || ICON_COLORS[0].class;
};

// Icon Selector Component
export const IconSelector = ({ value, onChange, color = 'indigo', onColorChange }) => {
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('business');
  
  const selectedIcon = ALL_ICONS.find(i => i.name === value);
  const IconComponent = selectedIcon?.icon || Building2;
  const colorClass = getColorClass(color);
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={`w-16 h-16 rounded-xl border-2 flex items-center justify-center transition-all hover:scale-105 ${colorClass}`}
          data-testid="icon-selector-trigger"
        >
          <IconComponent className="w-8 h-8" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Choose an Icon</DialogTitle>
        </DialogHeader>
        
        {/* Color Selection */}
        <div className="space-y-2">
          <p className="text-sm text-slate-600">Color</p>
          <div className="flex flex-wrap gap-2">
            {ICON_COLORS.map((c) => (
              <button
                key={c.name}
                onClick={() => onColorChange?.(c.name)}
                className={`w-8 h-8 rounded-lg border-2 transition-all ${c.class} ${color === c.name ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
              />
            ))}
          </div>
        </div>
        
        {/* Category Tabs */}
        <div className="flex flex-wrap gap-1 mt-4">
          {Object.keys(ICON_LIBRARY).map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1 text-xs rounded-full capitalize transition-colors ${
                activeCategory === cat 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        
        {/* Icons Grid */}
        <div className="grid grid-cols-6 gap-2 mt-4 max-h-64 overflow-y-auto">
          {ICON_LIBRARY[activeCategory].map((item) => {
            const Icon = item.icon;
            const isSelected = value === item.name;
            return (
              <button
                key={item.name}
                onClick={() => {
                  onChange(item.name);
                  setOpen(false);
                }}
                className={`p-3 rounded-lg border transition-all hover:scale-105 ${
                  isSelected 
                    ? `${colorClass} border-current` 
                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-300'
                }`}
                title={item.label}
              >
                <Icon className="w-6 h-6 mx-auto" />
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Display Icon Component (for showing selected icon)
export const DisplayIcon = ({ iconName, color = 'indigo', size = 'md' }) => {
  const colorClass = getColorClass(color);
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };
  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
  };
  
  // Get the icon component once during render - this is stable and not created during render
  const iconData = ALL_ICONS.find(i => i.name === iconName);
  const Icon = iconData?.icon || Building2;
  
  return (
    <div className={`${sizeClasses[size]} rounded-xl border flex items-center justify-center ${colorClass}`}>
      <Icon className={iconSizes[size]} />
    </div>
  );
};

export default IconSelector;
